/**
 * Live wallpaper settings panel.
 *
 * A floating panel over the desktop rather than a modal, and that is the whole
 * point: a wallpaper with a dozen force sliders is only tunable if you can
 * watch it react while you drag. A modal — or the Wallpaper app window — covers
 * the thing being configured.
 *
 * Chrome only. Rows come from settings-form.ts, shared with the widget dialog.
 */
import type { ModuleManifest, SettingSpec } from "@arclog/module-sdk";
import { osStore } from "./store";
import { uiApi, fireWallpaperAction } from "./api";
import { resolveSettings, visibleSpecs } from "./settings.mjs";
import { buildSettingRow, wrapSettingRow, type RowHandle } from "./settings-form";

const POS_KEY = "arclog.wallpaper.panelPos";

/** Singleton — a second panel would fight the first over the same values. */
let open$: { close(): void; moduleId: string } | null = null;

export function wallpaperSettingsSpecs(moduleId: string): SettingSpec[] {
  const m = osStore.wallpaperModules().find((x) => x.id === moduleId);
  return m?.wallpaper?.settings || [];
}

/** True when this wallpaper has anything to show — gates the menu entry. */
export function wallpaperHasSettings(moduleId: string): boolean {
  return wallpaperSettingsSpecs(moduleId).length > 0;
}

function loadPos(): { x: number; y: number } {
  try {
    const raw = JSON.parse(localStorage.getItem(POS_KEY) || "null");
    if (raw && typeof raw.x === "number" && typeof raw.y === "number") return raw;
  } catch {
    /* a corrupt position must not stop the panel opening */
  }
  return { x: Math.max(24, window.innerWidth - 360), y: 96 };
}

export function closeWallpaperSettingsPanel(): void {
  open$?.close();
}

export function openWallpaperSettingsPanel(moduleId?: string): void {
  const id = moduleId || osStore.wallpaperId;
  const specs = wallpaperSettingsSpecs(id);
  if (!specs.length) return;

  // Re-opening for the same wallpaper just focuses; for a different one,
  // replace rather than stack.
  if (open$) {
    if (open$.moduleId === id) return;
    open$.close();
  }

  const manifest: ModuleManifest | undefined = osStore
    .wallpaperModules()
    .find((x) => x.id === id);

  const el = document.createElement("div");
  el.className = "wsp";
  const pos = loadPos();
  // Clamp into view: a position saved on a wider monitor would otherwise put
  // the panel off-screen with no way to drag it back.
  el.style.left = `${Math.min(Math.max(0, pos.x), Math.max(0, window.innerWidth - 280))}px`;
  el.style.top = `${Math.min(Math.max(0, pos.y), Math.max(0, window.innerHeight - 120))}px`;

  const head = document.createElement("div");
  head.className = "wsp-head";
  const title = document.createElement("div");
  title.className = "wsp-title";
  title.textContent = manifest?.name || "Wallpaper";
  const closeBtn = document.createElement("button");
  closeBtn.type = "button";
  closeBtn.className = "wsp-x";
  closeBtn.setAttribute("aria-label", "Close");
  closeBtn.textContent = "×";
  head.append(title, closeBtn);

  const body = document.createElement("div");
  body.className = "wsp-body";

  const foot = document.createElement("div");
  foot.className = "wsp-foot";

  const handles: RowHandle[] = [];
  const destroyHandles = () => {
    for (const h of handles.splice(0)) {
      try {
        h.destroy();
      } catch {
        /* one control failing to tear down must not strand the rest */
      }
    }
  };

  /* Keys that gate another row's visibility. ONLY these re-render — doing it
     on every write would rip a slider out from under the pointer mid-drag. */
  const controllers = new Set<string>();
  for (const s of specs) if (s.showWhen) controllers.add(s.showWhen);

  /**
   * `type:"button"` is an ACTION, not a value: it never touches
   * `osStore.setWallpaperSetting`, so there is nothing here that could leave
   * a stale "pressed" state behind after a remount — a fresh `renderRows()`
   * just builds a fresh, unpressed button. `fireWallpaperAction` reaches the
   * mounted wallpaper's `ctx.wallpaper.settings.onChange` directly (see
   * api.ts) without writing anything to the persisted settings bag.
   */
  function buildButtonRow(s: SettingSpec): HTMLElement {
    const btn = uiApi.button({
      label: s.label,
      onClick: () => fireWallpaperAction(id, s.key),
    });
    return wrapSettingRow(btn, "wsp", s.help);
  }

  function renderRows(): void {
    destroyHandles();
    body.innerHTML = "";
    const values = resolveSettings(specs, osStore.getWallpaperSettings(id));
    for (const s of visibleSpecs(specs, values)) {
      if (s.type === "button") {
        body.appendChild(buildButtonRow(s));
        continue;
      }
      const built = buildSettingRow({
        spec: s,
        value: values[s.key],
        prefix: "wsp",
        // A reload key is NOT written live: persisting per pixel of drag would
        // churn localStorage for values the module cannot use yet. It commits
        // once, on release.
        onChange: s.reload
          ? () => {}
          : (v) => {
              osStore.setWallpaperSetting(id, s.key, v, false);
              if (controllers.has(s.key)) renderRows();
            },
        onCommit: s.reload
          ? (v) => osStore.setWallpaperSetting(id, s.key, v, true)
          : undefined,
      });
      if (!built) continue;
      handles.push(built.handle);
      body.appendChild(built.row);
    }
  }

  /* ── Dragging ─────────────────────────────────────────────────────────── */
  let dragging = false;
  let dx = 0;
  let dy = 0;
  head.addEventListener("pointerdown", (e) => {
    if (e.target === closeBtn) return;
    dragging = true;
    dx = e.clientX - el.offsetLeft;
    dy = e.clientY - el.offsetTop;
    head.setPointerCapture(e.pointerId);
  });
  head.addEventListener("pointermove", (e) => {
    if (!dragging) return;
    el.style.left = `${e.clientX - dx}px`;
    el.style.top = `${e.clientY - dy}px`;
  });
  const endDrag = () => {
    if (!dragging) return;
    dragging = false;
    try {
      localStorage.setItem(
        POS_KEY,
        JSON.stringify({ x: el.offsetLeft, y: el.offsetTop })
      );
    } catch {
      /* a full localStorage must not break dragging */
    }
  };
  head.addEventListener("pointerup", endDrag);
  head.addEventListener("pointercancel", endDrag);

  /* ── Lifecycle ────────────────────────────────────────────────────────── */
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") close();
  };
  // The wallpaper can change under us (gallery, another tab). Re-render against
  // the new schema, or close when the new one has none.
  const unsub = osStore.subscribe(() => {
    if (osStore.wallpaperId !== id) close();
  });

  function close(): void {
    document.removeEventListener("keydown", onKey);
    unsub();
    destroyHandles();
    el.remove();
    if (open$?.moduleId === id) open$ = null;
  }

  closeBtn.addEventListener("click", close);
  document.addEventListener("keydown", onKey);

  foot.appendChild(
    uiApi.button({
      label: "Reset to defaults",
      variant: "ghost",
      onClick: () => {
        osStore.resetWallpaperSettings(id);
        renderRows();
      },
    })
  );

  renderRows();
  el.append(head, body, foot);
  document.body.appendChild(el);
  open$ = { close, moduleId: id };
}
